<template>
  <div class="animated fadeIn">

    <h2 v-show="createUserPending" class="u-padding-bottom-20">Updating, please wait ...</h2>
    <pulse-loader :loading="createUserPending" color="#489AAC" size="50px"></pulse-loader>

    <div>
      <b-form class="add-user-form">
        <b-row>
          <b-col md="9" sm="12" class="mb-4">
            <h3 class="card-title mb-0">Create New User</h3>
          </b-col>
        </b-row>
        <b-card>
          <b-row>
            <b-col sm="12">
              <b-form-group label-size='lg' label-class='adduserlabel' label='State' label-for='user-state'>
                <b-form-select
                  data-vv-name="stateName"
                  v-validate="'required'"
                  data-vv-as="stateName"
                  size="lg"
                  :options="stateAndAbbrev"
                  id='stateName'
                  v-model='stateName'
                >
                </b-form-select>
                <span v-show="inputValidationErrors.has('stateName')" class="text-info">{{ inputValidationErrors.first('stateName') }}</span>
              </b-form-group>

              <b-form-group label-size='lg' label-class='adduserlabel' label='Primary Organization' label-for='primaryOrganization' :horizontal='false'>
                <b-form-select size="lg"
                  data-vv-name="primaryOrganization"
                  v-validate="'required'"
                  data-vv-as="Primary Organization"
                  :options="primaryOrganizations" id='primaryOrganization' v-model='primaryOrganization'>
                </b-form-select>
                <span v-show="inputValidationErrors.has('primaryOrganization')" class="text-info">{{ inputValidationErrors.first('primaryOrganization') }}</span>
              </b-form-group>

              <!-- User Form -->
              <NewUserForm
                v-for="(user, id) in newUsers"
                :key="id"
                :id="id"
                :formUser="user"
                :isSubmitLoading="isSubmitLoading"
                @submit="setUser($event, id)"
              >
                <template #form-header>
                  User {{ id + 1 }}
                </template>
              </NewUserForm>
              <div class="add-user-form__button-group">
                <b-button variant="secondary" v-if="newUsers.length > 1" @click="cancelUser">Cancel</b-button>
                <b-button variant="success" :disabled="newUsers.length > 20" @click="addNewUser">Add Another User</b-button>
              </div>

              <b-button
                class="button--submit"
                variant="success"
                :disabled="createUserPending || emailSentInProcess"
                @click="submit"
              >
                Submit
              </b-button>
              <b-button
                class="button--submit"
                variant="success"
                :disabled="createUserPending || emailSentInProcess"
                @click="submitAndSendWelcomeEmails"
              >
                Submit and Send Welcome Email(s)
              </b-button>

              <b-alert
                fade
                :show="createUserFailure"
                :variant="'danger'">
                <span v-html="apiResponseMessage(createUserFailure, true)">
                </span>
              </b-alert>
              <b-alert
                fade
                :show="emailSentSuccess || emailSentFailure"
                :variant="emailSentFailure ? 'danger' : 'success'">
                <span v-html="apiResponseMessage(emailSentSuccess || emailSentFailure, emailSentFailure)"></span>
                <span>
                  Navigating to view user <b>{{createUserSuccess.fullName}}</b> in {{countDown}} seconds...
                </span>
              </b-alert>
            </b-col>
          </b-row>
        </b-card>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { user as UserModel } from '@/api/models'
import NewUserForm from '@/views/t1/Admin/NewUserForm'

export default {
  name: 'add-user',

  components: {
    NewUserForm
  },

  data () {
    return {
      newUsers: [
        Object.assign(UserModel, {
          membership: {
            isManager: false,
            isOrgAdmin: false,
            isCareCoordinator: false
          }
        })
      ],
      stateName: '',
      primaryOrganization: '',
      isSubmitLoading: false,
      shouldSendWelcomeEmail: false,
      countDown: 5
    }
  },

  computed: {
    ...mapGetters({
      stateAndAbbrev: 'getStateAndAbbrev',
      organizations: 'getAllOrganizations',
      createUserSuccess: 'createUserSuccess',
      createUserPending: 'createUserPending',
      createUserFailure: 'createUserFailure',
      emailSentSuccess: 'getEmailSentSuccess',
      emailSentFailure: 'getEmailSentFailure',
      emailSentInProcess: 'getEmailSentInProcess'
    }),

    primaryOrganizations () {
      const organizationOptions = this.organizations
        .filter((org) => {
          return !this.stateName || (org.mailingStateAbbreviation === this.stateName)
        })
        .map((org) => {
          return { value: org.id, text: org.title }
        })

      organizationOptions.sort((a, b) => a.text > b.text ? 1 : -1)
      return organizationOptions
    }
  },

  watch: {
    createUserSuccess (newValue) {
      if (newValue.id && !this.shouldSendWelcomeEmail) {
        this.goToFirstCreatedUserView(newValue.id)
      }
    },

    stateAndAbbrev: {
      handler (newValue) {
        if (!this.stateName && newValue.length) {
          this.stateName = this.stateAndAbbrev[0].value
        }
      },
      deep: true,
      immediate: true
    }
  },

  beforeMount () {
    this.$store.dispatch('getOrganizations', {})
    this.$validator.extend('uniqueUserEmail', {
      getMessage: field => 'User email already exists. Please enter different email',
      validate: (value) => {
        return this.newUsers.filter((user) => user.emailAddress === value).length <= 1
      }
    })
  },

  beforeDestroy () {
    this.$store.commit('resetAddUser')
  },

  methods: {
    setUser (user, index) {
      this.newUsers[index] = user
    },

    goToFirstCreatedUserView (userId) {
      this.$router.push({ path: `/admin/users/${userId}` })
    },

    async submit () {
      this.isSubmitLoading = true
      this.$store.commit('SET_USER_STATE', { field: 'createUserFailure', value: false })

      const res = await this.$validator.validate()
      this.isSubmitLoading = false
      if (!res) {
        return
      }

      // batch create users
      this.newUsers = this.newUsers.map((user) => {
        user.primaryOrganization = this.primaryOrganization

        delete user.id
        return user
      })

      return this.$store.dispatch('bulkCreateUsers', {
        users: this.newUsers
      })
    },

    async submitAndSendWelcomeEmails () {
      this.shouldSendWelcomeEmail = true
      const createdUsers = await this.submit()

      if (this.createUserFailure || !createdUsers || !createdUsers.length) {
        return
      }

      const url = process.env.NODE_ENV === 'production'
        ? 'lighthouse'
        : 'dev.lighthouse'
      const users = createdUsers.map((user) => ({
        id: user.id,
        email: user.emailAddress
      }))
      await this.$store.dispatch('bulkSendWelcomeEmail', {
        users,
        environment: url
      })

      setInterval(() => {
        this.countDown--

        if (this.countDown === 0) {
          this.$store.commit('setEmailSentSuccess', false)
          this.goToFirstCreatedUserView(users[0].id)
        }
      }, 1000)
    },

    cancelUser () {
      this.newUsers.splice(this.newUsers.length - 1, 1)
    },

    addNewUser () {
      this.newUsers.push(UserModel)
    },

    apiResponseMessage: function (message, isFailure) {
      if (isFailure) {
        message = this.$utils.errorToHtml(message)
      }

      return '<p>' + message + '</p>'
    }
  }
}
</script>

<style lang="scss" scoped>
.add-user-form {
  &__button-group {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 20px;
  }

  .button--submit {
    display: block;
    margin-bottom: 10px;

    &:first-of-type {
      margin-top: 50px;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/views/t1/Admin/AddUser.vue